/**
 * Grouping utility functions for the Assessment Planning Dashboard.
 * These pure functions handle grouping measure items by section or by goal/objective hierarchy.
 */

/**
 * Groups items by sectionName.
 * @param {Array} items - Flat array of measure items with sectionName field
 * @returns {Array} Array of { sectionName: string, items: Array } sorted alphabetically by sectionName
 */
export function groupItemsBySection(items) {
  const groups = new Map();
  for (const item of items) {
    const key = item.sectionName || "";
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(item);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([sectionName, sectionItems]) => ({
      sectionName,
      items: sectionItems,
    }));
}

/**
 * Groups items by goal and objective into a two-level hierarchy.
 * @param {Array} items - Flat array of measure items with goalId and objectiveId fields
 * @param {Array} goals - Goals tree (array of { id, description, objectives: [{ id, description }] })
 * @returns {Array} Array of { goalId, goalDescription, objectiveGroups: [{ objectiveId, objectiveDescription, items }] }
 */
export function groupItemsByGoalObjective(items, goals) {
  const goalMap = new Map();

  // Build lookup from goals tree
  const goalLookup = new Map();
  for (const goal of goals) {
    goalLookup.set(goal.id, goal);
  }

  for (const item of items) {
    const goalId = item.goalId || "__ungrouped__";
    if (!goalMap.has(goalId)) {
      const goal = goalLookup.get(goalId);
      goalMap.set(goalId, {
        goalId,
        goalDescription: goal?.description || goalId,
        objectiveMap: new Map(),
      });
    }
    const goalGroup = goalMap.get(goalId);
    const objId = item.objectiveId || "__no_objective__";
    if (!goalGroup.objectiveMap.has(objId)) {
      goalGroup.objectiveMap.set(objId, []);
    }
    goalGroup.objectiveMap.get(objId).push(item);
  }

  // Convert to sorted array structure
  return Array.from(goalMap.values())
    .sort((a, b) => a.goalDescription.localeCompare(b.goalDescription))
    .map((goalGroup) => ({
      goalId: goalGroup.goalId,
      goalDescription: goalGroup.goalDescription,
      objectiveGroups: Array.from(goalGroup.objectiveMap.entries()).map(
        ([objectiveId, objItems]) => {
          // Find objective description from goals tree
          const goal = goalLookup.get(goalGroup.goalId);
          const obj = goal?.objectives?.find((o) => o.id === objectiveId);
          return {
            objectiveId,
            objectiveDescription:
              objectiveId === "__no_objective__"
                ? "No objective"
                : obj?.description || objectiveId,
            items: objItems,
          };
        }
      ),
    }));
}

/**
 * Status-to-color mapping for status pills.
 */
const STATUS_COLORS = {
  not_started: "primary",
  in_progress: "warning",
  submitted: "success",
};

/**
 * Determines the status pill color for a measure item.
 * @param {Object} item - A measure item with `status` and `group` fields
 * @param {string} item.status - One of "not_started", "in_progress", "submitted"
 * @param {string} item.group - The group the item belongs to (e.g., "overdue", "needs_attention", "complete")
 * @returns {string} The color string: "danger", "primary", "warning", or "success"
 */
export function getStatusColor(item) {
  if (item.group === "overdue") {
    return "danger";
  }
  return STATUS_COLORS[item.status] || "primary";
}
